import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { OWGameResponse } from "@devour/client";
import ProfileCard from "@/components/shared/ProfileCard";
import LoginModal from "@/components/modals/LoginModal";
import GoVipLevelsModal from "@/components/modals/GoVipLevelsModal";
import GoVipLeaderboardsModal from "@/components/modals/GoVipLeaderboardsModal";
import { IStore } from "@/redux/defaultStore";
import { useGetTransactionsPaginated } from "@/hooks/useGetTransactionsPaginated";
import { useGetUserProfile } from "@/hooks/useGetUserProfile";
import { useGetUserLevel } from "@/hooks/useGetUserLevel";
import { useGetOverwolfSummary } from "@/hooks/useGetOverwolfSummary";
import { useGetUserLeaderboardRank } from "@/hooks/useGetUserLeaderboardRank";

interface GameHeroSectionProps {
    game: OWGameResponse;
    userStats: any;
    hideProfileCard?: boolean;
    hideDescription?: boolean;
}

const GameHeroSection: React.FC<GameHeroSectionProps> = ({ game, userStats, hideProfileCard = false, hideDescription = false }) => {
    // Get authentication state
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);
    const currentUser = useSelector((store: IStore) => store.metaStore.currentUser);
    const isAuthenticated = !!fullToken;

    // Login modal state
    const [showLoginModal, setShowLoginModal] = useState(false);

    // Level rewards modal state
    const [showLevelRewardsModal, setShowLevelRewardsModal] = useState(false);

    // Leaderboard modal state
    const [showLeaderboardModal, setShowLeaderboardModal] = useState(false);

    // Expand/collapse state for mobile description
    const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
    const [isMobile, setIsMobile] = useState(false);

    // Check if we're on mobile
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 768);
        };
        checkMobile();
        window.addEventListener("resize", checkMobile);
        return () => window.removeEventListener("resize", checkMobile);
    }, []);

    // Get current fuel balance using the transactions hook
    const { data: transactionsData } = useGetTransactionsPaginated(
        fullToken,
        currentUser?.user?.id,
    );

    // Get user profile data
    const { data: userProfile } = useGetUserProfile();

    // Get user level data
    const { data: accountLevel } = useGetUserLevel(
        fullToken,
        currentUser?.user?.id,
    );

    // Get Overwolf summary data
    const { data: overwolfSummary } = useGetOverwolfSummary();

    // Get user's leaderboard rank
    const { data: rankData } = useGetUserLeaderboardRank(fullToken);

    // Format fuel balance from transactions data
    const fuelBalance = transactionsData?.balanceInUsd
        ? Intl.NumberFormat("en-us", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(transactionsData.balanceInUsd) : "0.00";

    // Extract Steam data if available
    const steamData = (game as any).steamData || {};
    // Use background_raw (highest quality) with fallbacks
    const heroImage = steamData.backgroundRaw || steamData.background_raw ||
                      steamData.background ||
                      steamData.headerImage || steamData.header_image ||
                      game.steamLogoUrl;
    const gameIcon = steamData.capsuleImage || steamData.capsule_image || game.steamLogoUrl;
    const genres = steamData.genres?.join(", ") || "Action";
    const developers = steamData.developers?.join(", ") || game.publisher || "Unknown Developer";
    const releaseDate = steamData.releaseDate || steamData.release_date?.date
        ? new Date(steamData.releaseDate || steamData.release_date?.date).getFullYear() : "2024";

    // Get the description text
    const fullDescription = steamData.shortDescription || steamData.short_description || steamData.description ||
                           `Join the ultimate ${game.name} experience on DevourPlay. Complete quests, earn XP, and unlock exclusive rewards.`;

    // Check if we're in cockpit view (hideProfileCard is a good indicator)
    const isInCockpit = hideProfileCard;

    // Truncate description for mobile or cockpit view (approximately 2 lines worth)
    const MAX_MOBILE_LENGTH = 150;
    const shouldTruncate = (isMobile || isInCockpit) && fullDescription.length > MAX_MOBILE_LENGTH;
    const displayDescription = shouldTruncate && !isDescriptionExpanded
        ? `${fullDescription.substring(0, MAX_MOBILE_LENGTH).trim()}...`
        : fullDescription;

    return (
        <div className="game-hero">
            {/* Hero Background Image */}
            <div className="game-hero__background">
                {heroImage
                    ? <>
                        <img
                            src={heroImage}
                            alt={`${game.name} background`}
                        />
                        <div className="game-hero__overlay" />
                    </>
                    : <>
                        <div className="game-hero__background-placeholder">
                            <span>{game.name}</span>
                        </div>
                        <div className="game-hero__overlay" />
                    </>
                }
            </div>

            {/* Hero Content */}
            <div className="game-hero__content">
                <div className="game-hero__info">
                    {/* Game Details - Logo above title */}
                    <div className="game-hero__details">
                        {/* Game Logo */}
                        {gameIcon
                            ? <img
                                src={gameIcon}
                                alt={`${game.name} logo`}
                                className="game-hero__logo"
                            />
                            : <div className="game-hero__logo-placeholder">
                                <span>{game.name?.substring(0, 2).toUpperCase()}</span>
                            </div>
                        }

                        <h1 className="game-hero__title">{game.name || "Game Title"}</h1>
                        {!hideDescription &&
                            <div className="game-hero__description-wrapper">
                                <p className={`game-hero__description ${isDescriptionExpanded ? "expanded" : ""}`}>
                                    {displayDescription}
                                </p>
                                {shouldTruncate &&
                                    <button
                                        className="game-hero__read-more"
                                        onClick={() => setIsDescriptionExpanded(!isDescriptionExpanded)}
                                    >
                                        {isDescriptionExpanded ? "Read less" : "Read more"}
                                    </button>
                                }
                            </div>
                        }
                        <div className="game-hero__meta">
                            <span className="game-hero__genre">{genres}</span>
                            <span className="game-hero__separator">•</span>
                            <span className="game-hero__developer">{developers}</span>
                            <span className="game-hero__separator">•</span>
                            <span className="game-hero__release">Released {releaseDate}</span>
                        </div>
                    </div>
                </div>

                {/* User Profile Card - Mobile Layout */}
                {!hideProfileCard &&
                    <div className="game-hero__profile">
                        <ProfileCard
                            fuelBalance={fuelBalance}
                            userProfile={userProfile}
                            accountLevel={accountLevel}
                            overwolfSummary={overwolfSummary}
                            isAuthenticated={isAuthenticated}
                            isLoading={false}
                            onLoginClick={() => setShowLoginModal(true)}
                            onLevelsClick={() => setShowLevelRewardsModal(true)} // Gift Icon -> Level Rewards
                            onBadgeClick={() => setShowLeaderboardModal(true)} // Level Badge -> Leaderboard
                            forceMobileView={true} // Always use mobile view on games page
                        />
                    </div>
                }
            </div>

            {/* Login Modal */}
            <LoginModal
                isOpen={showLoginModal}
                onClose={() => setShowLoginModal(false)}
            />

            {/* Level Rewards Modal */}
            <GoVipLevelsModal
                isOpen={showLevelRewardsModal}
                toggle={() => setShowLevelRewardsModal(false)}
            />

            {/* Leaderboard Modal */}
            <GoVipLeaderboardsModal
                isOpen={showLeaderboardModal}
                onClose={() => setShowLeaderboardModal(false)}
                leaderboardRank={rankData?.rank || 0}
                shouldShowUserRank={accountLevel?.level >= 2}
            />
        </div>
    );
};

export default GameHeroSection;